import { useMemo } from "react";
import { MenuTrigger, Menu, Item, ActionButton } from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleFill } from "react-bootstrap-icons";
import clsx from "clsx";
import {
  getSenderEmail,
} from "../../../../utils/helperUtil";
import { invokeAddContactFromEmail } from "../../../../utils/NavigationUtils";
import styles from "./email_sender_contact_badge.module.css";

export interface EmailSenderContactBadgeProps {
  /** List row + optional MESSAGE fields merged for status / senderContactId. */
  messageRow: any;
  mailFrom: string;
  sender: string;
  /** Hide for concepts / non-email types. */
  enabled: boolean;
  className?: string;
}

/** Incoming email header: warn + action only when sender is not linked to a contact. */
export function EmailSenderContactBadge({
  messageRow,
  mailFrom,
  sender,
  enabled,
  className,
}: Readonly<EmailSenderContactBadgeProps>) {
  const { t } = useTranslation();

  const decision = useMemo(() => {
    if (!enabled || !messageRow) {
      return { show: false as const };
    }
    // Keep the badge narrow in scope: only email-like rows, only incoming.
    const status = String(messageRow?.status ?? "").trim();
    if (status === "256") {
      return { show: false as const };
    }
    const type = String(messageRow?.type ?? "").trim();
    if (type && !["2", "3", "7"].includes(type)) {
      return { show: false as const };
    }
    const email = getSenderEmail(mailFrom);
    if (!email) {
      return { show: false as const };
    }
    const senderContactIdRaw = messageRow?.senderContactId;
    if (senderContactIdRaw != null && String(senderContactIdRaw).trim() && String(senderContactIdRaw).trim() !== "0") {
      // BE already handles the "known contact" visual treatment; don't add any extra UI here.
      return { show: false as const };
    }
    return {
      show: true as const,
      variant: "unknown" as const,
      email,
    };
  }, [enabled, mailFrom, messageRow]);

  if (!decision.show) {
    return null;
  }

  const addLabel = t("create_contact_from_sender", {
    ns: "layout_components",
    defaultValue: "Create new contact…",
  });

  return (
    <span className={clsx(styles.wrap, className)}>
      <MenuTrigger>
        <ActionButton
          UNSAFE_className={styles.trigger}
          aria-labelledby={t("sender_not_linked_contact", {
            ns: "layout_components",
            defaultValue:
              "Sender not matched to a contact (no linked contact id). Create a contact to link this address.",
          })}
          aria-label={t("sender_not_linked_contact", {
            ns: "layout_components",
            defaultValue:
              "Sender not matched to a contact (no linked contact id). Create a contact to link this address.",
          })}
        >
          <ExclamationTriangleFill size={16} aria-hidden />
        </ActionButton>
        <Menu onAction={(key) => {
          if (key === "add") {
            invokeAddContactFromEmail(decision.email, {
              senderDisplayName: String(sender ?? "").trim() || undefined,
            });
          }
        }}
        >
          <Item key="add">{addLabel}</Item>
        </Menu>
      </MenuTrigger>
    </span>
  );
}
